import React from 'react';
import {
  CandidateTaxonomyUtil,
  CondensedHeader,
  CondensedLarge,
  Grid,
  GridColumn,
  GridRow,
  useApi,
  AccordionsItem,
  AccordionsItemHeader,
  Layout,
  LayoutItem,
  Badge,
  AccordionsItemSection,
  Gutter,
  Accordions,
  LegalTechSkillsDisplay,
  Paragraph,
  ExperienceTaxonomyLegalSkillsDisplay,
} from '@axiom/ui';
import type { Candidate, CandidateRedux } from '@axiom/validation';

import { TaxonomyApi } from '../../api/taxonomy-api';
import { CandidateLegalTechSkillsApi } from '../../api/candidate-legal-tech-skills-api';
import { CandidateExperiencesApi } from '../../api/candidate-experiences-api';

export type TalentProfileDetailSkillsTypes = {
  candidate: CandidateRedux | Candidate;
  name: string;
};

export const TalentProfileDetailSkills = ({
  candidate,
  name,
}: TalentProfileDetailSkillsTypes) => {
  const [{ data: taxonomy }, { data: experiences }, { data: legalTechSkills }] =
    useApi(
      TaxonomyApi.readTaxonomy(),
      CandidateExperiencesApi.readExperiences(candidate.id),
      CandidateLegalTechSkillsApi.readLegalTechSkills(candidate.id)
    );

  const aggregatedExperienceTaxonomy = (experiences ?? []).flatMap(
    experience => experience.experienceTaxonomy
  );
  const aggregatedLegalSkills = Object.values(
    CandidateTaxonomyUtil.groupExperienceTaxonomyByFocusArea(
      aggregatedExperienceTaxonomy
    )
  ).flatMap(
    focusAreas =>
      focusAreas?.skillGroups.flatMap(({ legalSkills }) => legalSkills) ?? []
  );
  const aggregatedLegalTechSkills = Object.values(
    CandidateTaxonomyUtil.getDisplayableLegalTechSkills(legalTechSkills)
  ).flat();
  const aggregatedGeneralSkills = [
    ...new Set(
      (experiences ?? []).flatMap(experience => experience.generalSkills ?? [])
    ),
  ];

  return (
    <Grid name={name}>
      <GridRow>
        <GridColumn>
          <CondensedHeader>Skills</CondensedHeader>
        </GridColumn>
      </GridRow>
      <GridRow gutterVertical="16px">
        <GridColumn>
          {aggregatedLegalTechSkills.length +
            aggregatedGeneralSkills.length +
            aggregatedLegalSkills.length ===
          0 ? (
            <Paragraph name="TALENT_PROFILE_DETAIL_SKILLS_EMPTY">
              {candidate.calculatedDisplayName} has not added any skills.
            </Paragraph>
          ) : (
            <Accordions name="TALENT_PROFILE_DETAIL_SKILLS_ACCORDIONS">
              {aggregatedLegalSkills.length > 0 && (
                <AccordionsItem name="TALENT_PROFILE_DETAIL_SKILLS_LEGAL_SKILLS_PENDO">
                  <AccordionsItemHeader name="TALENT_PROFILE_DETAIL_SKILLS_LEGAL_SKILLS_HEADER">
                    <Layout direction="horizontal" horizontalGutter="8px">
                      <LayoutItem>
                        <CondensedHeader>Legal Skills</CondensedHeader>
                      </LayoutItem>
                      <LayoutItem>
                        <Badge
                          background="light orange"
                          name="TALENT_PROFILE_DETAIL_SKILLS_LEGAL_SKILLS_COUNT"
                        >
                          {aggregatedLegalSkills.length}
                        </Badge>
                      </LayoutItem>
                    </Layout>
                  </AccordionsItemHeader>
                  <AccordionsItemSection>
                    <Gutter bottom="24px" />
                    <ExperienceTaxonomyLegalSkillsDisplay
                      name="EXPERIENCE_TAXONOMY_LEGAL_SKILLS_DISPLAY"
                      experienceTaxonomy={aggregatedExperienceTaxonomy}
                      taxonomy={taxonomy}
                    />
                  </AccordionsItemSection>
                </AccordionsItem>
              )}
              {aggregatedLegalTechSkills.length > 0 && (
                <AccordionsItem name="TALENT_PROFILE_DETAIL_SKILLS_LEGAL_TECH_SKILLS_PENDO">
                  <AccordionsItemHeader name="TALENT_PROFILE_DETAIL_SKILLS_LEGAL_TECH_SKILLS_HEADER">
                    <Layout direction="horizontal" horizontalGutter="8px">
                      <LayoutItem>
                        <CondensedHeader>Legal Tech Skills</CondensedHeader>
                      </LayoutItem>
                      <LayoutItem>
                        <Badge
                          background="light orange"
                          name="TALENT_PROFILE_DETAIL_SKILLS_LEGAL_TECH_SKILLS_COUNT"
                        >
                          {aggregatedLegalTechSkills.length}
                        </Badge>
                      </LayoutItem>
                    </Layout>
                  </AccordionsItemHeader>
                  <AccordionsItemSection>
                    <Gutter bottom="24px" />
                    <LegalTechSkillsDisplay
                      name="LEGAL_TECH_SKILLS_DISPLAY"
                      legalTechSkills={legalTechSkills}
                      taxonomy={taxonomy}
                      condensed
                    />
                  </AccordionsItemSection>
                </AccordionsItem>
              )}
              {aggregatedGeneralSkills.length > 0 && (
                <AccordionsItem name="TALENT_PROFILE_DETAIL_SKILLS_GENERAL_SKILLS_PENDO">
                  <AccordionsItemHeader name="TALENT_PROFILE_DETAIL_SKILLS_GENERAL_SKILLS_HEADER">
                    <Layout direction="horizontal" horizontalGutter="8px">
                      <LayoutItem>
                        <CondensedHeader>General Skills</CondensedHeader>
                      </LayoutItem>
                      <LayoutItem>
                        <Badge
                          background="light orange"
                          name="TALENT_PROFILE_DETAIL_SKILLS_GENERAL_SKILLS_COUNT"
                        >
                          {aggregatedGeneralSkills.length}
                        </Badge>
                      </LayoutItem>
                    </Layout>
                  </AccordionsItemHeader>
                  <AccordionsItemSection>
                    <Gutter bottom="24px" />
                    <CondensedHeader>General Skills</CondensedHeader>
                    <CondensedLarge name="TALENT_PROFILE_DETAIL_SKILLS_GENERAL_SKILLS_DISPLAY">
                      {aggregatedGeneralSkills
                        .sort()
                        .map(gs => taxonomy.generalSkills[gs]?.displayName)
                        .join('; ')}
                    </CondensedLarge>
                  </AccordionsItemSection>
                </AccordionsItem>
              )}
            </Accordions>
          )}
          <Gutter bottom="8px" />
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
